import AccountStore from "../stores/AccountStore"
import HierarchySettingsStore from "../stores/HierarchySettingsStore"
import { IPayType, IPayGroup, IPayrollPayGroupData, IUserPayrollData, IJobDocument, IHierarchyItemForAccountLevel, IHierarchyItem, IUserEffectiveDateDocument, IDate, IUserDocument } from "../models"
import * as utils from "./utils"
import * as hierarchyHelper from "./hierarchyHelper"
import strings from "../localization/strings"
import * as payrollHelper from "./payrollHelper"
import moment from "moment"
import * as timeHelper from "./timeHelper"

export function getUserPayrollPayGroupDataByReferenceDate(user: IUserDocument, userEffectiveDates: IUserEffectiveDateDocument[], referenceDate: IDate, byLockedTime = false): IPayrollPayGroupData {
    const effectiveDate = getClosestAfterReferenceDate(userEffectiveDates, referenceDate, byLockedTime)
    return effectiveDate ?
        {
            PayrollHierarchyItemId: effectiveDate.PrevPayrollHierarchyItemId,
            PayrollPayGroupId: effectiveDate.PrevPayGroup.Id
        } :
        {
            PayrollHierarchyItemId: user.PayrollHierarchyItemId,
            PayrollPayGroupId: user.PayrollPayGroupId
        }
}

export function getClosestAfterReferenceDate(userEffectiveDates: IUserEffectiveDateDocument[], referenceDate: IDate, byLockedTime: boolean) {
    userEffectiveDates = userEffectiveDates.sort((a, b) => moment(timeHelper.keepConvertUTCToLocal(a.PrevPayrollSettingsEndDate)).diff(moment(timeHelper.keepConvertUTCToLocal(b.PrevPayrollSettingsEndDate))))
    return userEffectiveDates.find(i => moment(referenceDate).isBefore(timeHelper.keepConvertUTCToLocal(byLockedTime ? i.LockTimeEndDate : i.PrevPayrollSettingsEndDate)))
}

export function getPayrollPayGroupDataByUserPayrollData(userPayrollData: IUserPayrollData, defaultJob: IJobDocument): IPayrollPayGroupData {
    return {
        PayrollHierarchyItemId: userPayrollData.PayrollHierarchyItemId,
        PayrollPayGroupId: getPayGroupIdByUserPayrollData(userPayrollData, defaultJob)
    }
}

export function getPayGroupIdByUserPayrollData(userPayrollData: IUserPayrollData, defaultJob: IJobDocument): string {
    return userPayrollData.PayrollPayGroupId ?
        userPayrollData.PayrollPayGroupId :
        AccountStore.getEnablePayGroupAssignEmployeeLevel() ?
            userPayrollData.DefaultJobId && defaultJob && defaultJob.HierarchyItemSettings && defaultJob.HierarchyItemSettings.PayGroupSettings && defaultJob.HierarchyItemSettings.PayGroupSettings.PayGroupId ?
                defaultJob.HierarchyItemSettings.PayGroupSettings.PayGroupId :
                getInheritedPayGroupIdByHierarchyId(userPayrollData.DefaultPunchHierarchyId) :
            getInheritedPayGroupIdByHierarchyId(userPayrollData.PayrollHierarchyItemId)
}

export function getPayGroupIdByPayrollPayGroupData(payrollPayGroupData: IPayrollPayGroupData): string {
    if (payrollPayGroupData.PayrollPayGroupId) {
        return payrollPayGroupData.PayrollPayGroupId
    }
    return !AccountStore.getEnablePayGroupAssignEmployeeLevel() &&
        getInheritedPayGroupIdByHierarchyId(payrollPayGroupData.PayrollHierarchyItemId)
}

export function getInheritedPayGroupIdByHierarchyId(hierarchyItemId: string): string {
    const inheritedHierarchy = hierarchyHelper.getInheritedHierarchySettingItems(hierarchyHelper.getHierarchyItemById(hierarchyItemId)).PayGroupSettings
    return inheritedHierarchy ?
        inheritedHierarchy.HierarchyItemSettings.PayGroupSettings.PayGroupId :
        AccountStore.getCurrentAccount().HierarchyItemSettings.PayGroupSettings.PayGroupId
}

export function getPayGroupByPayrollPayGroupData(payrollPayGroupData: IPayrollPayGroupData): IPayGroup {
    return getPayGroupById(getPayGroupIdByPayrollPayGroupData(payrollPayGroupData))
}

export function getDefaultPayType(payGroupId) {
    const account = AccountStore.getCurrentAccount()
    const defaultPayGroup = account.PayGroups.find((payGroup) => payGroup.Id === payGroupId)
    const accountPayTypes = account.PayTypes ? account.PayTypes : []
    const defaultPayType = accountPayTypes.find((payType) => payType.Id === defaultPayGroup.DefaultPayTypeId)
    return defaultPayType
}

export function getLinkedPayTypes(payGroupId): IPayType[] {
    const account = AccountStore.getCurrentAccount()
    const defaultPayGroup = account.PayGroups.find((payGroup) => payGroup.Id === payGroupId)
    const linkedPayTypeIds = defaultPayGroup.LinkedPayTypeIds ? defaultPayGroup.LinkedPayTypeIds : []
    const accountPayTypes = account.PayTypes ? account.PayTypes : []
    const linkedPayTypes = []
    linkedPayTypeIds.length > 0 &&
        linkedPayTypeIds.forEach((linkedPayTypeId) => {
            const payType = accountPayTypes.find((payType) => payType.Id === linkedPayTypeId)
            payType && linkedPayTypes.push(payType)
        })
    return linkedPayTypes
}

export function getPayTypeById(payTypeId) {
    const account = AccountStore.getCurrentAccount()
    const accountPayTypes = account.PayTypes ? account.PayTypes : []
    const payType = accountPayTypes.find((payType) => payType.Id === payTypeId)
    return payType
}

export function getPayTypeNameById(payTypeId) {
    const payType = getPayTypeById(payTypeId)
    return payType ? payType.Name : strings.invalid
}

export function getPayGroupById(payGroupId: string): IPayGroup {
    return AccountStore.getCurrentAccount().PayGroups.find(item => item.Id === payGroupId)
}

export function getPayrollSelectedPayGroup(allowNonSelect: boolean = false) {
    const validPayrollPayGroups = payrollHelper.getValidPayrollPayGroups()

    const storedSelectedPayGroupId = utils.getCookie("selected_paygroup_id")
    const storedSelectedPayGroup = storedSelectedPayGroupId && validPayrollPayGroups.find(i => i.Id === storedSelectedPayGroupId)
    const selectedPayGroup = storedSelectedPayGroup ?
        storedSelectedPayGroup :
        allowNonSelect && storedSelectedPayGroupId === AccountStore.NonSelectItemValue ?
            null :
            validPayrollPayGroups[0]
    return selectedPayGroup
}

export function getInheritedPaygroupByGlobalHierarchy(): IPayGroup {
    const hierarchyId = HierarchySettingsStore.getGlobalLevelItemFilterId()
    return getPayGroupByPayrollPayGroupData({
        PayrollHierarchyItemId: hierarchyId,
        PayrollPayGroupId: getInheritedPayGroupIdByHierarchyId(hierarchyId)
    })
}

export function getOTPolicyId(employeePayrollHierarchyId: string, employeeDefaultPunchHierarchyId: string, employeeDefaultJob: IJobDocument, employeePayrollPayGroupId: string, employeeOTPolicyId: string): string {
    if (employeeOTPolicyId)
        return employeeOTPolicyId

    let hierarchyJobItem: IHierarchyItemForAccountLevel | IHierarchyItem | IJobDocument = employeeDefaultJob
    if (!hierarchyJobItem) {
        hierarchyJobItem = employeeDefaultPunchHierarchyId ?
            hierarchyHelper.getHierarchyItemById(employeeDefaultPunchHierarchyId) :
            null
    }
    const inherittedHierarchyJobItem = hierarchyHelper.getInheritedHierarchySettingItems(hierarchyJobItem).OTPolicySettings
    const hierarchyOTPolicySettings = inherittedHierarchyJobItem ?
        inherittedHierarchyJobItem.HierarchyItemSettings :
        AccountStore.getCurrentAccount().HierarchyItemSettings
    if (hierarchyOTPolicySettings && hierarchyOTPolicySettings.OTPolicySettings && hierarchyOTPolicySettings.OTPolicySettings.OTPolicyId) {
        return hierarchyOTPolicySettings.OTPolicySettings.OTPolicyId
    }

    const userPayrollData: IUserPayrollData = {
        Id: "",
        PayrollHierarchyItemId: employeePayrollHierarchyId,
        DefaultPunchHierarchyId: employeeDefaultPunchHierarchyId,
        DefaultJobId: employeeDefaultJob ? employeeDefaultJob.Id : null,
        PayrollPayGroupId: employeePayrollPayGroupId
    }
    const payGroupId = getPayGroupIdByUserPayrollData(userPayrollData, employeeDefaultJob)
    const payGroup = getPayGroupById(payGroupId)
    return payGroup ? payGroup.OTPolicyId : null
}
